import { createRef } from 'react';
import { reviewActionOr, reviewChangesOr, updateElementAudit } from './audit';
import { collectionChanges } from './change-api';
import { getAutoCompleteInput, inputValueSelection } from './controls';
import { Audit, CoderReviewQuestion, ReturnItem } from './domain';
import { formApi } from './form';
import { listApi } from './list';
import { fetchRequest } from './';
import { WorksheetContext } from './visit-context';
import { isReviewWorkflowState } from './workflow-api';
import { WorksheetFormApi } from './worksheet-form-api';
import { worksheetMessage } from './worksheet-service';

const service = 'codereviewquestion';

export const addReviewQuestionRequest = fetchRequest({
  method: 'post',
  body: (args: CoderReviewQuestion) =>
    worksheetMessage(args, 'addquestion', service),
});

export const updateReviewQuestionRequest = fetchRequest({
  method: 'post',
  body: (args: CoderReviewQuestion) =>
    worksheetMessage(args, 'updatequestion', service),
});

export const deleteReviewQuestionRequest = fetchRequest({
  method: 'post',
  body: (args: CoderReviewQuestion) =>
    worksheetMessage(args, 'deletequestion', service),
});

export interface ReviewQuestionApi
  extends WorksheetFormApi<CoderReviewQuestion> {}

export function reviewQuestionApi(
  context: WorksheetContext
): ReviewQuestionApi {
  const { worksheet, status, config, coderReviewQuestions, readonly } = context;
  const { worksheetId } = worksheet;
  const { codeReviewQuestionList } = config;

  const defaultFilter = isReviewWorkflowState() ? 'all' : 'active';
  const list = listApi({
    defaultFilter,
    all: coderReviewQuestions,
    id: (a: CoderReviewQuestion) => a.coderReviewQuestionId,
    equals: (a, b) => a.coderReviewQuestionId === b.coderReviewQuestionId,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
  });

  let editing: CoderReviewQuestion;
  function editItem(v: CoderReviewQuestion) {
    api.onEdit();

    list.actions.edit(v);
    editing = v;

    form.setValues({
      worksheetId,
      question: v.question,
      reply: v.reply,
    });

    reason.setListValue((a: ReturnItem) => a?.code === v?.reasonCode);
  }

  function addItem(v: CoderReviewQuestion) {
    async function request(update?: (v: any) => void) {
      if (update) {
        update(v);
      }

      v.coderReviewQuestionId = editing?.coderReviewQuestionId;

      const response = await (editing
        ? updateReviewQuestionRequest(v)
        : addReviewQuestionRequest(v));

      if (response.success) {
        const item = response.result;

        if (update) {
          update(item);
        }

        if (editing) {
          list.actions.replace(
            item,
            (v) => v.coderReviewQuestionId === editing.coderReviewQuestionId
          );
        } else {
          list.actions.add(item);
        }

        focusInput('');
      } else {
        setStatus((response.errors || []).join(' '));
      }

      editing = null;
    }

    const edit = list.data.getState().editing;
    if (edit) {
      reviewChangesOr(
        {
          title: 'Review Changes For Question ',
          original: edit,
          update: v,
          changeReasons: {
            actualCPTCode: config.auditDownCode,
            targetCPTCode: config.auditDownCode,
          },
          fieldNames: [],
          onComplete: (a: Audit) => {
            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    } else {
      reviewActionOr(
        {
          title: 'Add Review Question ',
          list: config.auditDownCode,
          onComplete: (a) => {
            a.action = 'CREATE';
            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    }
  }

  function removeItem(v: CoderReviewQuestion) {
    async function request() {
      const response = await deleteReviewQuestionRequest({
        worksheetId: worksheet.worksheetId,
        coderReviewQuestionId: v.coderReviewQuestionId,
      });

      if (response.success) {
        list.actions.remove(v);
      } else {
        setStatus((response.errors || []).join(' '));
      }
    }
    reviewActionOr(
      {
        title: 'Remove Review Question ',
        list: context.config.auditDownCode,
        onComplete: (a) => {
          a.action = 'DELETE';
          // v.audit = a;
          request();
        },
      },
      request
    );
  }

  const initialValues = {
    reason: null,
    question: null,
    reply: '',
  };

  const setStatus = status.getState().setStatus;

  const form: any = formApi<CoderReviewQuestion>({
    disabled: readonly,
    values: initialValues,
    fields: {
      reason: {
        label: 'Reason',
        list: codeReviewQuestionList,
        listLabel: (v: ReturnItem) => v.description,
        required: true,
        inputRef: createRef(),
        onEnterValid: () => {
          form.submit();
        },
      },
      question: {
        label: 'Question',
        required: true,
        onEnter: () => {
          form.submit();
        },
      },
      reply: {
        label: 'Reply',
        required: false,
        multiline: true,
        onEnter: () => {
          form.submit();
        },
      },
    },
    valid: (v) => {
      return !!v.reason && !!v.question;
    },
    onSubmit: async (v: CoderReviewQuestion) => {
      addItem({
        worksheetId,
        reasonCode: v.reason?.code,
        description: v.reason.description,
        question: v.question,
        reply: v.reply,
      });
    },
    resetValues: () => initialValues,
  });

  const { reason } = form.controls;

  const inputElement = getAutoCompleteInput(reason.inputRef);
  const focusInput = inputValueSelection(inputElement);
  
  const listChanges = collectionChanges(list, {});

  function setPhysician(userId: String) {}

  const api = {
    form,
    list,
    listChanges,
    context,
    addItem,
    editItem,
    removeItem,
    setPhysician,
    onEdit: () => null,
  };
  return api;
}
